/**
 * Submission Actions for Customer Confidence Call
 * Handles form submission and data processing
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getAccessToken } from "@elixir/auth";
import { Guid } from "guid-typescript";
import jwtDecode from "jwt-decode";
import { getDataFromSessionStorage, validateBackendUrl } from "../../../config/configHelper";
import { HttpService } from "../../../library/http/http.service";
import { CALL_TYPE_TO_SUBMISSION_TYPE, DEFAULT_SUBMISSION_TYPE, RM2_COMMERCIAL_SEGMENT, } from "../ConfidenceCallTypeConstants";
import { normalizeAzureSubscriptionGuid } from "../subscriptionGuid";
const resolveStakeholderUpn = (user) => {
    // Prefer canonical directory UPN values from search results.
    // Fall back to other common email/UPN fields to avoid empty UPNs in payloads.
    return (user.userPrincipalName ||
        user.upn ||
        user.mail ||
        user.userEmail ||
        user.uniqueName ||
        "");
};
const resolveStakeholderObjectId = (user) => {
    return (user.id ||
        user.objectId ||
        user.aadObjectId ||
        user.oid ||
        "");
};
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
const getSubmitterFromToken = () => __awaiter(void 0, void 0, void 0, function* () {
    const token = yield getAccessToken();
    if (!token) {
        throw new Error("Unable to acquire an access token for submission.");
    }
    const decoded = jwtDecode(token);
    const submitterEmail = decoded.preferred_username ||
        decoded.upn ||
        decoded.email ||
        decoded.unique_name ||
        "";
    const name = decoded.name || "";
    const nameParts = name.split(" ").filter(Boolean);
    const submitterFirstName = decoded.given_name ||
        (nameParts.length > 0 ? nameParts[0] : "");
    const submitterLastName = decoded.family_name ||
        (nameParts.length > 1 ? nameParts.slice(1).join(" ") : "");
    return {
        submitterTenantId: decoded.tid || "",
        submitterEmail,
        submitterFirstName,
        submitterLastName,
        aadObjectId: decoded.oid || decoded.objectid || decoded.sub || "",
    };
});
const buildSubmissionPayload = (data, submitter) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y;
    const clientSubmissionId = ((_a = data.clientSubmissionId) === null || _a === void 0 ? void 0 : _a.trim()) || "";
    if (!Guid.isGuid(clientSubmissionId) ||
        clientSubmissionId.toLowerCase() === EMPTY_GUID) {
        throw new Error("A valid submission reference is required.");
    }
    const selectedIncidents = ((_b = data.basicInfo) === null || _b === void 0 ? void 0 : _b.selectedIncidents) || [];
    const selectedSupportRequestNumbers = ((_c = data.basicInfo) === null || _c === void 0 ? void 0 : _c.selectedSupportRequestNumbers) || [];
    const selectedIncidentOutageMap = ((_d = data.basicInfo) === null || _d === void 0 ? void 0 : _d.selectedIncidentOutageMap) || {};
    const selectedProductIds = ((_e = data.productComponent) === null || _e === void 0 ? void 0 : _e.workloads) || [];
    const selectedProducts = ((_f = data.productComponent) === null || _f === void 0 ? void 0 : _f.selectedProducts) || [];
    const technicalDepth = data.technicalDepth || ((_g = data.contextRationale) === null || _g === void 0 ? void 0 : _g.technicalDepth) || "";
    const customerName = ((_h = data.basicInfo) === null || _h === void 0 ? void 0 : _h.customerName) || "";
    const linkedIncidents = selectedIncidents.map((incidentId) => {
        var _a;
        return ({
            incidentNumber: ((_a = incidentId.replace(/^(icm|outage)-/i, "").match(/\d+/)) === null || _a === void 0 ? void 0 : _a[0]) ||
                incidentId.replace(/^(icm|outage)-/i, ""),
            sourceSystem: selectedIncidentOutageMap[incidentId] ? "OUTAGE" : "ICM",
        });
    });
    const supportTicketTitleMap = ((_j = data.basicInfo) === null || _j === void 0 ? void 0 : _j.supportTicketTitleMap) || {};
    const references = selectedSupportRequestNumbers.map((supportTicket) => ({
        referenceType: "SupportTicket",
        referenceId: supportTicket,
        description: supportTicketTitleMap[supportTicket] || "",
        sourceSystem: "SR",
    }));
    const productsWithSource = selectedProducts.length > 0
        ? selectedProducts
        : selectedProductIds.map((product) => ({ id: product, name: product, source: "Picker" }));
    const primaryProductIndex = Math.max(productsWithSource.findIndex((product) => product.source !== "Manual"), 0);
    const products = productsWithSource.map((product, index) => ({
        id: product.id,
        name: product.name,
        isPrimary: index === primaryProductIndex,
        source: product.source || "Picker",
    }));
    const countryName = ((_k = data.basicInfo) === null || _k === void 0 ? void 0 : _k.customerCountry) || "";
    // UEM normalizes the segment for RM2; legacy submissions retain the account segment.
    const customerSegment = ((_l = data.basicInfo) === null || _l === void 0 ? void 0 : _l.customerSegment) || RM2_COMMERCIAL_SEGMENT;
    const countryId = ((_m = data.basicInfo) === null || _m === void 0 ? void 0 : _m.customerCountryId) || EMPTY_GUID;
    const countryAlpha2 = ((_o = data.basicInfo) === null || _o === void 0 ? void 0 : _o.customerCountryAlpha2) || "";
    const countryAlpha3 = ((_p = data.basicInfo) === null || _p === void 0 ? void 0 : _p.customerCountryAlpha3) || "";
    const countryIsEU = Boolean((_q = data.basicInfo) === null || _q === void 0 ? void 0 : _q.customerCountryIsEU);
    // RM2 matches the country by its Alpha-3 code, and UEM returns 400 when it is
    // missing rather than inferring one from the display name. Fail here with an
    // actionable message instead of sending a request that cannot succeed.
    if (!countryAlpha3.trim()) {
        throw new Error("A country code is required. Select the customer location again so a valid country can be resolved.");
    }
    const submissionType = CALL_TYPE_TO_SUBMISSION_TYPE[((_r = data.callType) === null || _r === void 0 ? void 0 : _r.typeKey) || ""] ||
        DEFAULT_SUBMISSION_TYPE;
    const outageSubmissionType = CALL_TYPE_TO_SUBMISSION_TYPE.outage || DEFAULT_SUBMISSION_TYPE;
    const normalizedAzureSubscriptionId = normalizeAzureSubscriptionGuid((_s = data.basicInfo) === null || _s === void 0 ? void 0 : _s.azureSubscriptionId);
    const requiresAzureSubscription = submissionType === outageSubmissionType;
    if (requiresAzureSubscription &&
        !normalizedAzureSubscriptionId) {
        throw new Error("Select one valid Azure subscription before submitting the confidence call.");
    }
    return Object.assign(Object.assign({ clientSubmissionId,
        submissionType }, (requiresAzureSubscription
        ? { azureSubscriptionId: normalizedAzureSubscriptionId }
        : {})), { accountInfo: {
            companyName: customerName,
            accountId: ((_t = data.basicInfo) === null || _t === void 0 ? void 0 : _t.customerTpid) || "",
            mSSalesId: ((_u = data.basicInfo) === null || _u === void 0 ? void 0 : _u.customerMsSalesId) || "",
            accountSegment: customerSegment,
            accountType: "Customer",
        }, desiredOutcome: ((_v = data.contextRationale) === null || _v === void 0 ? void 0 : _v.desiredOutcome) || "", description: ((_w = data.contextRationale) === null || _w === void 0 ? void 0 : _w.escalationReasonSummary) || "", businessImpact: ((_x = data.contextRationale) === null || _x === void 0 ? void 0 : _x.businessImpact) || "", customerInfo: {
            customerEmail: "",
            customerFirstName: "",
            customerLastName: "",
            customerPhone: "",
            microsoftAccount: "",
            preferredLanguage: "English",
            country: countryName,
            countryAlpha2,
            languageAlpha2: "en",
            customerSegmentName: customerSegment,
            customerType: "Customer",
        }, language: {
            id: null,
            name: "English",
            alpha2: "en",
        }, country: {
            id: countryId,
            name: countryName,
            alpha2: countryAlpha2,
            alpha3: countryAlpha3,
            isEU: countryIsEU,
        }, references, stakeholders: (((_y = data.contextRationale) === null || _y === void 0 ? void 0 : _y.stakeholders) || []).map((user) => {
            const directoryUser = user;
            const resolvedUpn = resolveStakeholderUpn(directoryUser);
            return {
                objectId: resolveStakeholderObjectId(directoryUser),
                upn: resolvedUpn,
                displayName: directoryUser.displayName ||
                    directoryUser.name ||
                    resolvedUpn ||
                    "",
            };
        }), details: {
            expectedTechnicalDepth: technicalDepth,
            linkedIncidents,
            products,
        }, attachments: [], submitterInfo: {
            submitterEmail: submitter.submitterEmail,
            submitterFirstName: submitter.submitterFirstName,
            submitterLastName: submitter.submitterLastName,
            aadObjectId: submitter.aadObjectId,
            submitterTenantId: submitter.submitterTenantId,
        } });
};
/**
 * Validate EU boundaries for confidence call submission.
 * Calls the Escalation Manager metadata API to determine if the customer's
 * country falls within the EU data boundary.
 * @param country - The customer country name
 * @returns Promise with EU boundary validation response
 */
export const validateEuBoundaries = (country) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    try {
        const urlResult = validateBackendUrl(getDataFromSessionStorage("globalFrontdoorbaseurl"));
        if (!urlResult.valid) {
            return {
                message: `Invalid backend URL configuration: ${urlResult.reason}`,
                result: { isEU: false, httpStatusCode: "400" },
                errors: [{ errorMessage: urlResult.reason, field: "globalFrontdoorbaseurl" }],
                httpStatusCode: "400",
            };
        }
        const http = new HttpService();
        const body = { country };
        const response = yield http.post(urlResult.origin, "/api/v1/metadata/validate-eu-boundaries", undefined, body);
        if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
            return response.data;
        }
        return response === null || response === void 0 ? void 0 : response.data;
    }
    catch (e) {
        const error = e;
        return (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data;
    }
});
/**
 * Build a user-visible error message from a failed submission response.
 *
 * The downstream Escalation Manager dependency surfaces failures as
 * `application/problem+json` (title/detail/traceId), while local validation and
 * other paths use the `ApiResponse` shape (message/errors). This normalizes both
 * and appends the traceId when present so failures can be correlated with the
 * backend telemetry without exposing sensitive detail.
 */
const buildSubmissionErrorMessage = (body, fallbackError) => {
    const errors = (body === null || body === void 0 ? void 0 : body.errors) || (body === null || body === void 0 ? void 0 : body.Errors);
    const errorsText = Array.isArray(errors)
        ? errors
            .map((error) => typeof error === "string"
            ? error
            : error.errorMessage ||
                error.ErrorMessage ||
                error.message ||
                error.Message ||
                "")
            .filter(Boolean)
            .join("; ")
        : "";
    const titleDetail = [body === null || body === void 0 ? void 0 : body.title, body === null || body === void 0 ? void 0 : body.detail].filter(Boolean).join(". ");
    const fallbackMessage = (fallbackError === null || fallbackError === void 0 ? void 0 : fallbackError.message) || "";
    const baseMessage = (body === null || body === void 0 ? void 0 : body.message) ||
        (body === null || body === void 0 ? void 0 : body.Message) ||
        titleDetail ||
        errorsText ||
        fallbackMessage ||
        "Failed to submit confidence call.";
    const responseTraceId = (body === null || body === void 0 ? void 0 : body.traceId) || (body === null || body === void 0 ? void 0 : body.TraceId);
    return responseTraceId
        ? `${baseMessage} (trace id: ${responseTraceId})`
        : baseMessage;
};
const normalizeSubmissionWarnings = (warnings) => (warnings || [])
    .map((warning) => {
    var _a, _b;
    return ({
        code: String((_b = (_a = warning.code) !== null && _a !== void 0 ? _a : warning.Code) !== null && _b !== void 0 ? _b : "").trim(),
        field: (warning.field || warning.Field || "").trim() || undefined,
        message: (warning.message || warning.Message || "").trim(),
    });
})
    .filter((warning) => warning.code || warning.message);
const isIncompleteSubmissionWarning = (warning) => warning.code !== "1100";
const classifySubmissionFailure = (statusCode) => {
    if (statusCode === 400) {
        return "validation";
    }
    const definitelyNotSubmittedStatuses = [
        401, 403, 404, 405, 409, 413, 415, 422, 429,
    ];
    if (definitelyNotSubmittedStatuses.includes(statusCode)) {
        return "failed";
    }
    return "uncertain";
};
const resolveSubmissionStatusCode = (bodyStatusCode, transportStatusCode) => {
    const parsedBodyStatusCode = Number(bodyStatusCode);
    if (Number.isFinite(parsedBodyStatusCode) &&
        parsedBodyStatusCode > 0) {
        return parsedBodyStatusCode;
    }
    const parsedTransportStatusCode = Number(transportStatusCode);
    return Number.isFinite(parsedTransportStatusCode)
        ? parsedTransportStatusCode
        : 0;
};
const normalizeSubmissionResponse = (responseData, transportStatusCode, fallbackError) => {
    var _a, _b, _c, _d, _e;
    const httpStatusCode = resolveSubmissionStatusCode((_a = responseData === null || responseData === void 0 ? void 0 : responseData.httpStatusCode) !== null && _a !== void 0 ? _a : responseData === null || responseData === void 0 ? void 0 : responseData.HttpStatusCode, transportStatusCode);
    const result = (responseData === null || responseData === void 0 ? void 0 : responseData.result) || (responseData === null || responseData === void 0 ? void 0 : responseData.Result) || {};
    const warnings = normalizeSubmissionWarnings(result.warnings || result.Warnings);
    const requestId = ((_b = result.escalationRequestNumber) === null || _b === void 0 ? void 0 : _b.trim()) ||
        ((_c = result.EscalationRequestNumber) === null || _c === void 0 ? void 0 : _c.trim()) ||
        ((_d = result.id) === null || _d === void 0 ? void 0 : _d.trim()) ||
        ((_e = result.Id) === null || _e === void 0 ? void 0 : _e.trim()) ||
        "";
    const traceId = (responseData === null || responseData === void 0 ? void 0 : responseData.traceId) || (responseData === null || responseData === void 0 ? void 0 : responseData.TraceId);
    const responseDetails = Object.assign(Object.assign(Object.assign({}, (requestId ? { requestId } : {})), (traceId ? { traceId } : {})), { warnings });
    if (httpStatusCode < 200 || httpStatusCode >= 300) {
        return Object.assign({ success: false, status: classifySubmissionFailure(httpStatusCode), error: buildSubmissionErrorMessage(responseData, fallbackError) }, responseDetails);
    }
    if (!requestId) {
        return Object.assign({ success: false, status: "uncertain", error: "The service accepted the submission but did not return an escalation request number." }, responseDetails);
    }
    return Object.assign({ success: true, status: warnings.some(isIncompleteSubmissionWarning)
            ? "partial"
            : "confirmed" }, responseDetails);
};
/**
 * Submit confidence call data to the API
 * @param data - The confidence call data to submit
 * @param isEU - Whether the case should be routed to the EU backend
 * @returns Promise with submission result
 */
export const submitConfidenceCall = (data, isEU) => __awaiter(void 0, void 0, void 0, function* () {
    var _b, _c;
    let submitter;
    let payload;
    try {
        submitter = yield getSubmitterFromToken();
    }
    catch (e) {
        return {
            success: false,
            status: "failed",
            error: buildSubmissionErrorMessage(undefined, e),
        };
    }
    try {
        payload = buildSubmissionPayload(data, submitter);
    }
    catch (e) {
        return {
            success: false,
            status: "validation",
            error: buildSubmissionErrorMessage(undefined, e),
        };
    }
    try {
        const http = new HttpService();
        const baseUrlKey = isEU ? "euFrontdoorbaseurl" : "globalFrontdoorbaseurl";
        const urlResult = validateBackendUrl(getDataFromSessionStorage(baseUrlKey));
        if (!urlResult.valid) {
            // Keep validation details internal; don't echo sessionStorage-derived content to UI.
            console.warn("Confidence call submission blocked due to invalid backend URL.", {
                reason: urlResult.reason,
                baseUrlKey,
            });
            return {
                success: false,
                status: "failed",
                error: "Failed to submit confidence call: invalid backend configuration.",
            };
        }
        const response = (yield http.post(urlResult.origin, "/api/create/escalationmanagersubmission", undefined, payload));
        return normalizeSubmissionResponse(response === null || response === void 0 ? void 0 : response.data, response === null || response === void 0 ? void 0 : response.status);
    }
    catch (e) {
        const error = e;
        return normalizeSubmissionResponse((_b = error.response) === null || _b === void 0 ? void 0 : _b.data, (_c = error.response) === null || _c === void 0 ? void 0 : _c.status, e);
    }
});
